import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Hero from '../components/Hero';
import MovieCard from '../components/MovieCard';
import TripsGrid from '../components/TripsGrid';
import MovieUploadModal from '../components/MovieUploadModal';
import { useMovies, useAllFootage, useTrips } from '../data/firestore';
import { useAuth } from '../AuthContext';
import SITE from '../site';

const toMillis = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  const n = new Date(ts).getTime();
  return Number.isNaN(n) ? 0 : n;
};

export default function Browse() {
  const { user } = useAuth();
  const { docs: movies, error } = useMovies();
  const { docs: footage } = useAllFootage();
  const { docs: trips } = useTrips();
  const [adding, setAdding] = useState(false);
  const [sort, setSort] = useState('newest');
  const navigate = useNavigate();
  const onNewTrip = user ? () => navigate('/trips') : undefined;

  const hero = useMemo(() => {
    if (!movies || movies.length === 0) return null;
    return movies.find((m) => m.featured) || movies[0];
  }, [movies]);

  const sortedMovies = useMemo(() => {
    const list = [...(movies || [])];
    list.sort((a, b) => {
      const da = toMillis(a.createdAt);
      const db = toMillis(b.createdAt);
      return sort === 'oldest' ? da - db : db - da;
    });
    return list;
  }, [movies, sort]);

  if (movies === null) {
    return <div className="cs-page">Loading…</div>;
  }

  if (movies.length === 0) {
    return (
      <div className="cs-page">
        <div className="cs-page-head">
          <h1>Browse</h1>
          {user && (
            <button className="cs-btn cs-btn-primary" onClick={() => setAdding(true)}>
              + Upload movie
            </button>
          )}
        </div>
        {error && <div className="cs-empty" style={{ marginBottom: 16 }}>{error}</div>}
        {!trips || trips.length === 0 ? (
          <div className="cs-empty">
            No movies yet.{user ? ' Upload your first edited surf film — pick the video file and (optionally) alternate soundtrack MP3s. Raw footage you upload to a trip shows up here too.' : ' Check back soon.'}
          </div>
        ) : (
          <div className="cs-rows" style={{ padding: 0 }}>
            <TripsGrid title="Your trips" trips={trips} assets={footage || []} onNewTrip={onNewTrip} />
          </div>
        )}
        {adding && <MovieUploadModal onClose={() => setAdding(false)} />}
      </div>
    );
  }

  // Section order is per-site (SITE.home): chadha leads with the movie grid,
  // nihirote surfaces trips first under the hero.
  const moviesSection = (
    <div className="cs-movies" style={{ marginBottom: 28 }}>
      <div className="cs-movies-toolbar">
        <label className="cs-help" htmlFor="cs-movie-sort" style={{ margin: 0 }}>
          Sort
        </label>
        <select
          id="cs-movie-sort"
          className="cs-input cs-input-sm"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
        {user && (
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setAdding(true)}>
            + Upload movie
          </button>
        )}
      </div>
      <div className="cs-movie-grid">
        {sortedMovies.map((m) => (
          <MovieCard key={m.id} movie={m} />
        ))}
      </div>
    </div>
  );
  const tripsSection = (
    <TripsGrid
      title="Trips"
      trips={trips || []}
      assets={footage || []}
      onNewTrip={onNewTrip}
    />
  );

  return (
    <div>
      <Hero movie={hero} />
      <div className="cs-rows">
        {SITE.home.tagline && (
          <p className="cs-help" style={{ margin: '0 0 20px', fontSize: 15 }}>
            {SITE.home.tagline}
          </p>
        )}
        {SITE.home.tripsBeforeMovies ? (
          <>
            <div style={{ marginBottom: 28 }}>{tripsSection}</div>
            {moviesSection}
          </>
        ) : (
          <>
            {moviesSection}
            {tripsSection}
          </>
        )}
      </div>
      {adding && <MovieUploadModal onClose={() => setAdding(false)} />}
    </div>
  );
}
